import { inject, Injectable, NgZone } from '@angular/core';
import { AuthService } from 'app/core/auth/auth.service';
import { SessionLockService } from 'app/core/auth/idle/session-lock.service';
import {
  getIdleTimeoutMs,
  IDLE_LAST_ACTIVITY_KEY,
  IDLE_LOCK_ENABLED_KEY,
  IDLE_LOCK_MINUTES_KEY,
  IDLE_LOGOUT_KEY,
  isIdleExpired,
  markActivityNow,
  readIdleLockEnabled,
  readLastActivityAt,
} from 'app/core/auth/idle/idle.constants';

const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'wheel',
  'scroll',
  'touchstart',
  'pointerdown',
] as const;

/**
 * Only scroll-type events are passive. Zone.js shares one native capture listener per event type
 * on `document`, so `passive` here would make every app capture listener for that type passive
 * and silently ignore their preventDefault() (keyboard navigation, dialogs).
 */
const PASSIVE_ACTIVITY_EVENTS: ReadonlySet<string> = new Set(['wheel', 'scroll', 'touchstart']);

const CHECK_INTERVAL_MS = 1000;

/** Keeps mousemove from hammering localStorage. */
const ACTIVITY_WRITE_THROTTLE_MS = 5000;

/**
 * Locks the screen after the user's idle timeout (Settings) without input, in every open tab.
 * Background API calls and websocket messages never count as activity.
 */
@Injectable({ providedIn: 'root' })
export class IdleService {
  private _zone = inject(NgZone);
  private _authService = inject(AuthService);
  private _sessionLock = inject(SessionLockService);

  private _started = false;
  private _lastWriteAt = 0;

  start(): void {
    if (this._started || typeof window === 'undefined') {
      return;
    }
    this._started = true;

    this._zone.runOutsideAngular(() => {
      for (const type of ACTIVITY_EVENTS) {
        document.addEventListener(type, this._onActivity, {
          capture: true,
          passive: PASSIVE_ACTIVITY_EVENTS.has(type),
        });
      }
      document.addEventListener('visibilitychange', this._check);
      window.addEventListener('focus', this._check);
      window.addEventListener('pageshow', this._check);
      window.addEventListener('storage', this._onStorage);
      setInterval(this._check, CHECK_INTERVAL_MS);
    });
  }

  private _onActivity = (): void => {
    if (!this._authService.accessToken || this._sessionLock.isLocked) {
      return;
    }
    // Input arriving after the deadline (e.g. waking a laptop) must not revive the session.
    if (isIdleExpired()) {
      this._check();
      return;
    }
    const now = Date.now();
    if (now - this._lastWriteAt < ACTIVITY_WRITE_THROTTLE_MS) {
      return;
    }
    this._lastWriteAt = now;
    markActivityNow();
  };

  private _onStorage = (event: StorageEvent): void => {
    if (event.key === IDLE_LOGOUT_KEY && event.newValue) {
      this._zone.run(() => {
        this._authService.logoutThisTabAfterIdle();
      });
      return;
    }
    if (
      event.key === IDLE_LAST_ACTIVITY_KEY ||
      event.key === IDLE_LOCK_ENABLED_KEY ||
      event.key === IDLE_LOCK_MINUTES_KEY
    ) {
      this._check();
    }
  };

  private _check = (): void => {
    if (!this._authService.accessToken || this._sessionLock.isLocked || !readIdleLockEnabled()) {
      return;
    }

    const last = readLastActivityAt();
    if (!last) {
      markActivityNow();
      return;
    }

    if (Date.now() - last >= getIdleTimeoutMs()) {
      this._zone.run(() => this._sessionLock.lock());
    }
  };
}
